import { TabPanel } from '@mui/lab';
import { Divider, Skeleton, Stack } from '@mui/material';
import { FormattedMessage } from 'react-intl';

import type { AltriaOffersNearbyStatus } from '@ocx/feat-local-offers';
import type { ExternalOfferFragment } from '@ocx/graphql';
import { Offer } from '@ocx/data-offers';
import { NoDataView, SizingMultiplier } from '@ocx/ui';
import { OfferCard } from '@ocx/ui-offers';

import { ExternalBannerCardController } from '../external-banner/external-banner-card.controller';
import { OfferCardController } from '../offer-card/offer-card.controller';
import { messages } from './offers-catalog-body.messages';

export type OffersCarouselPanelControllerProps = {
  id: string;
  offers: Offer[];
  banners: ExternalOfferFragment[];
  isCategoryLoading?: boolean;
  hasAltriaOffersNearby?: AltriaOffersNearbyStatus;
};

export const OffersCarouselPanelController = (props: OffersCarouselPanelControllerProps) => {
  const { id, offers, banners, isCategoryLoading = false, hasAltriaOffersNearby } = props;

  const hasContent = banners.length > 0 || offers.length > 0;
  const showBannersSkeleton = isCategoryLoading && !hasContent;
  const isCategoryEmpty = !isCategoryLoading && !hasContent;

  if (showBannersSkeleton) {
    return (
      <TabPanel value={id} sx={{ p: 0 }}>
        <Stack spacing={SizingMultiplier.sm} data-testid="external-banners-loading">
          <Skeleton variant="rounded" height={160} />
          <Skeleton variant="rounded" height={160} />
        </Stack>
      </TabPanel>
    );
  }

  if (isCategoryEmpty) {
    return (
      <TabPanel value={id} sx={{ p: 0 }}>
        <NoDataView title={<FormattedMessage {...messages['offers-catalog-body.no-offers-available-title']} />} />
      </TabPanel>
    );
  }

  return (
    <TabPanel value={id} sx={{ p: 0 }}>
      <Stack divider={<Divider variant="middle" />}>
        {banners.map((banner) => (
          <ExternalBannerCardController
            key={`${banner.bannerConfigurationUuid}_${banner.trackingId}`}
            banner={banner}
            hasAltriaOffersNearby={hasAltriaOffersNearby}
          />
        ))}
        {offers.map((offer) => (
          <OfferCardController key={offer.id} offer={offer}>
            {(props) => <OfferCard {...props} />}
          </OfferCardController>
        ))}
      </Stack>
    </TabPanel>
  );
};
